import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpDown,
  BadgeDollarSign,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Filter,
  Package,
  RefreshCw,
  Search,
  ShoppingBag,
  SlidersHorizontal,
  Store,
  Truck,
  X,
  XCircle,
} from "lucide-react";
import moment from "moment";
import { toast } from "react-toastify";
import Navbar from "../../../components/Navbar";
import OrdersRequest from "../../../requests/vendor/OrdersRequest";
import OrderStatusBadge from "../../../components/Orders/OrderStatusBadge";
import OrderActionMenu from "../../../components/Orders/OrderActionMenu";
import OrdersEmptyState from "../../../components/Orders/OrdersEmptyState";
import OrdersPagination from "../../../components/Orders/OrdersPagination";
import OrderDetailsModal from "../../../components/Orders/OrderDetailsModal";
import UpdateOrderStatusModal from "../../../components/Orders/UpdateOrderStatusModal";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
  FULFILLMENT_TYPE_OPTIONS,
  ORDER_STATUS_OPTIONS,
  PURCHASE_TYPE_OPTIONS,
  SORT_ORDER_OPTIONS,
  formatLabel,
} from "../../../utils/orderHelpers";

const DEFAULT_QUERY_STATE = {
  page: 1,
  perPage: 15,
  search: "",
  status: "",
  fulfillmentType: "",
  purchaseType: "",
  sortOrder: "desc",
  dateFrom: "",
  dateTo: "",
};

// Initials avatar for customer column
const CustomerAvatar = ({ name }) => {
  const initials = name
    ? name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("")
    : "?";

  const colors = [
    "bg-rose-100 text-rose-600",
    "bg-blue-100 text-blue-600",
    "bg-purple-100 text-purple-600",
    "bg-teal-100 text-teal-600",
    "bg-amber-100 text-amber-600",
    "bg-indigo-100 text-indigo-600",
  ];
  const color = colors[(name?.charCodeAt(0) ?? 0) % colors.length];

  return (
    <div
      className={`flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${color}`}
    >
      {initials}
    </div>
  );
};

const FilterSelect = ({ label, value, onChange, options, icon }) => (
  <div className="relative w-full lg:w-auto">
    <div className="flex absolute left-3 top-1/2 items-center text-gray-400 -translate-y-1/2 pointer-events-none">
      {icon}
    </div>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
      className="appearance-none pl-9 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors cursor-pointer w-full lg:min-w-[152px]"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
  </div>
);

const FulfillmentBadge = ({ type }) => {
  if (!type) return <span className="text-sm text-gray-400">—</span>;
  const isPickup = type.toLowerCase().includes("pickup");
  const Icon = isPickup ? Store : Truck;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
        isPickup
          ? "bg-purple-50 text-purple-700 ring-1 ring-purple-600/20"
          : "bg-sky-50 text-sky-700 ring-1 ring-sky-600/20"
      }`}
    >
      <Icon className="w-3 h-3" />
      {formatLabel(type)}
    </span>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, gradient, iconBg, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className={`relative overflow-hidden bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow`}
  >
    <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-40 pointer-events-none`} />
    <div className="relative z-10 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-1.5">{label}</p>
        <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
        {sub && <p className="mt-1.5 text-xs text-gray-400">{sub}</p>}
      </div>
      <div className={`flex-shrink-0 p-2.5 rounded-xl ${iconBg}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div className={`absolute -bottom-3 -right-3 w-20 h-20 rounded-full ${iconBg} opacity-20 blur-xl pointer-events-none`} />
  </motion.div>
);

const SkeletonRow = () => (
  <tr className="animate-pulse">
    <td className="px-5 py-4"><div className="w-32 h-5 bg-gray-100 rounded-lg" /></td>
    <td className="px-5 py-4">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 bg-gray-100 rounded-full flex-shrink-0" />
        <div className="space-y-1.5">
          <div className="w-28 h-3 bg-gray-100 rounded" />
          <div className="w-20 h-2.5 bg-gray-50 rounded" />
        </div>
      </div>
    </td>
    <td className="px-5 py-4"><div className="w-12 h-5 bg-gray-100 rounded-full" /></td>
    <td className="px-5 py-4"><div className="w-16 h-5 bg-gray-100 rounded-full" /></td>
    <td className="px-5 py-4"><div className="w-20 h-4 bg-gray-100 rounded" /></td>
    <td className="px-5 py-4"><div className="w-24 h-5 bg-gray-100 rounded-full" /></td>
    <td className="px-5 py-4"><div className="w-20 h-4 bg-gray-100 rounded" /></td>
    <td className="px-5 py-4 text-right"><div className="w-7 h-7 bg-gray-100 rounded-lg ml-auto" /></td>
  </tr>
);

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 450);

  const [showFilters, setShowFilters] = useState(false);
  const [queryState, setQueryState] = useState(DEFAULT_QUERY_STATE);

  const [viewOrder, setViewOrder] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [statusOrder, setStatusOrder] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);

  useEffect(() => {
    setQueryState((prev) => {
      if (prev.search === debouncedSearch) return prev;
      return { ...prev, search: debouncedSearch, page: 1 };
    });
  }, [debouncedSearch]);

  useEffect(() => {
    let ignore = false;
    const fetchOrders = async () => {
      setIsFetching(true);
      const params = {
        page: queryState.page,
        per_page: queryState.perPage,
        limit: queryState.perPage,
        sort_order: queryState.sortOrder,
        search: queryState.search || undefined,
        status: queryState.status || undefined,
        fulfillment_type: queryState.fulfillmentType || undefined,
        purchase_type: queryState.purchaseType || undefined,
        date_from: queryState.dateFrom || undefined,
        date_to: queryState.dateTo || undefined,
      };
      const result = await OrdersRequest.getOrders(params);
      if (ignore) return;
      if (result.success && result.data?.status_code === "000" && !result.data?.in_error) {
        const payload = result.data.data;
        setOrders(payload?.data || []);
        setPagination(payload?.pagination || null);
      } else {
        toast.error(result.data?.reason || result.message || "Failed to fetch orders");
      }
      setIsFetching(false);
      setIsInitialLoading(false);
    };
    fetchOrders();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryState, refreshKey]);

  // Stats computed from the currently loaded page of orders
  const stats = useMemo(() => {
    const pageRevenue = orders.reduce((sum, o) => sum + (Number(o.summary?.subtotal) || 0), 0);
    const pendingCount = orders.filter((o) => o.status === "pending_payment").length;
    const completedCount = orders.filter((o) => o.status === "completed").length;
    const cancelledCount = orders.filter((o) => o.status === "cancelled").length;
    return { pageRevenue, pendingCount, completedCount, cancelledCount };
  }, [orders]);

  const hasActiveFilters = Boolean(
    queryState.search || queryState.status || queryState.fulfillmentType ||
    queryState.purchaseType || queryState.dateFrom || queryState.dateTo
  );

  const advancedFilterCount = useMemo(
    () =>
      [queryState.fulfillmentType, queryState.purchaseType, queryState.dateFrom, queryState.dateTo]
        .filter(Boolean).length,
    [queryState.fulfillmentType, queryState.purchaseType, queryState.dateFrom, queryState.dateTo]
  );

  const updateFilter = (key, value) =>
    setQueryState((prev) => ({ ...prev, [key]: value, page: 1 }));

  const goToPage = (page) => setQueryState((prev) => ({ ...prev, page }));

  const handlePerPageChange = (perPage) =>
    setQueryState((prev) => ({ ...prev, perPage, page: 1 }));

  const clearFilters = () => {
    setSearchInput("");
    setQueryState((prev) => ({
      ...prev,
      search: "", status: "", fulfillmentType: "",
      purchaseType: "", dateFrom: "", dateTo: "", page: 1,
    }));
  };

  const handleManualRefresh = () => setRefreshKey((k) => k + 1);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Order code copied");
    } catch {
      toast.error("Failed to copy order code");
    }
  };

  const handleViewItems = (order) => { setViewOrder(order); setViewModalOpen(true); };
  const handleUpdateStatus = (order) => { setStatusOrder(order); setStatusModalOpen(true); };

  const handleStatusUpdated = (orderCode, newStatus) => {
    setOrders((prev) =>
      prev.map((o) => (o.order_code === orderCode ? { ...o, status: newStatus } : o))
    );
    setViewOrder((prev) =>
      prev && prev.order_code === orderCode ? { ...prev, status: newStatus } : prev
    );
  };

  return (
    <div className="min-h-screen bg-gray-50/70">
      <Navbar page="Orders" />

      {/* ─── Hero Banner ─── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#ff7f7f] to-pink-400 px-5 py-8 sm:px-8 sm:py-10">
        {/* decorative orbs */}
        <div className="absolute -top-10 -right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-12 -left-8 w-48 h-48 bg-pink-300/20 rounded-full blur-2xl pointer-events-none" />
        <div className="absolute top-4 right-40 w-20 h-20 bg-white/5 rounded-full blur-xl pointer-events-none" />

        <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between max-w-none">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 bg-white/20 rounded-2xl backdrop-blur-sm border border-white/30">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                Order Management
              </h1>
              <p className="mt-0.5 text-sm text-white/75">
                Track, manage and fulfil your customer orders in one place
              </p>
            </div>
          </div>

          <button
            onClick={handleManualRefresh}
            disabled={isFetching}
            className="self-start sm:self-auto inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-[#FF6C6C] bg-white rounded-xl shadow-md hover:bg-gray-50 transition-all disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="px-4 py-6 sm:px-6 sm:py-7 space-y-5">

        {/* ─── Stat Cards ─── */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <StatCard
            icon={Package}
            label="Total Orders"
            value={pagination?.total ?? (isInitialLoading ? "—" : 0)}
            sub={hasActiveFilters ? "Matching current filters" : "All time"}
            gradient="from-rose-50 to-transparent"
            iconBg="bg-rose-100 text-rose-500"
            delay={0}
          />
          <StatCard
            icon={BadgeDollarSign}
            label="Page Revenue"
            value={isInitialLoading ? "—" : formatCurrency(stats.pageRevenue)}
            sub="Current page subtotal"
            gradient="from-emerald-50 to-transparent"
            iconBg="bg-emerald-100 text-emerald-600"
            delay={0.05}
          />
          <StatCard
            icon={Clock}
            label="Pending"
            value={isInitialLoading ? "—" : stats.pendingCount}
            sub="Awaiting payment · this page"
            gradient="from-amber-50 to-transparent"
            iconBg="bg-amber-100 text-amber-600"
            delay={0.1}
          />
          <StatCard
            icon={CheckCircle2}
            label="Completed"
            value={isInitialLoading ? "—" : stats.completedCount}
            sub="Fulfilled orders · this page"
            gradient="from-teal-50 to-transparent"
            iconBg="bg-teal-100 text-teal-600"
            delay={0.15}
          />
        </div>

        {/* ─── Filter Bar ─── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
        >
          <div className="p-4 sm:p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              {/* Search */}
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by order code, customer, email or phone…"
                  className="block w-full pl-10 pr-9 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50/60 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] focus:bg-white transition-colors"
                />
                {searchInput && (
                  <button
                    onClick={() => setSearchInput("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Status */}
              <FilterSelect
                label="Status"
                value={queryState.status}
                onChange={(value) => updateFilter("status", value)}
                options={[{ value: "", label: "All Statuses" }, ...ORDER_STATUS_OPTIONS]}
                icon={<Filter className="w-4 h-4" />}
              />

              {/* Sort */}
              <FilterSelect
                label="Sort order"
                value={queryState.sortOrder}
                onChange={(value) => updateFilter("sortOrder", value)}
                options={SORT_ORDER_OPTIONS}
                icon={<ArrowUpDown className="w-4 h-4" />}
              />

              {/* More Filters toggle */}
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`inline-flex flex-shrink-0 items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition-colors whitespace-nowrap ${
                  showFilters || advancedFilterCount > 0
                    ? "bg-[#FF6C6C]/10 border-[#FF6C6C]/30 text-[#FF6C6C]"
                    : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filters
                {advancedFilterCount > 0 && (
                  <span className="inline-flex justify-center items-center w-5 h-5 text-[10px] font-bold text-white bg-[#FF6C6C] rounded-full">
                    {advancedFilterCount}
                  </span>
                )}
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex gap-1.5 items-center px-3 py-2.5 text-sm font-medium text-gray-400 hover:text-gray-700 whitespace-nowrap"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>

            {/* Advanced filters panel */}
            <AnimatePresence initial={false}>
              {showFilters && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="overflow-hidden"
                >
                  <div className="grid grid-cols-1 gap-3 pt-4 mt-4 border-t border-gray-100 sm:grid-cols-2 lg:grid-cols-4">
                    <FilterSelect
                      label="Fulfillment type"
                      value={queryState.fulfillmentType}
                      onChange={(value) => updateFilter("fulfillmentType", value)}
                      options={[{ value: "", label: "All Fulfillment Types" }, ...FULFILLMENT_TYPE_OPTIONS]}
                      icon={<Truck className="w-4 h-4" />}
                    />
                    <FilterSelect
                      label="Purchase type"
                      value={queryState.purchaseType}
                      onChange={(value) => updateFilter("purchaseType", value)}
                      options={[{ value: "", label: "All Purchase Types" }, ...PURCHASE_TYPE_OPTIONS]}
                      icon={<ShoppingBag className="w-4 h-4" />}
                    />
                    <div>
                      <label className="block mb-1.5 text-xs font-medium text-gray-500">From Date</label>
                      <input
                        type="date"
                        value={queryState.dateFrom}
                        onChange={(e) => updateFilter("dateFrom", e.target.value)}
                        className="px-3 py-2.5 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-xs font-medium text-gray-500">To Date</label>
                      <input
                        type="date"
                        value={queryState.dateTo}
                        onChange={(e) => updateFilter("dateTo", e.target.value)}
                        className="px-3 py-2.5 w-full text-sm rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors"
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 px-5 pb-4">
              {queryState.status && (
                <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium text-[#FF6C6C] bg-[#FF6C6C]/8 rounded-full border border-[#FF6C6C]/20">
                  Status: {formatLabel(queryState.status)}
                  <button onClick={() => updateFilter("status", "")} className="hover:text-[#FF6C6C]/70">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {queryState.fulfillmentType && (
                <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium text-sky-600 bg-sky-50 rounded-full border border-sky-200">
                  {formatLabel(queryState.fulfillmentType)}
                  <button onClick={() => updateFilter("fulfillmentType", "")} className="hover:text-sky-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {queryState.purchaseType && (
                <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-full border border-purple-200">
                  {formatLabel(queryState.purchaseType)}
                  <button onClick={() => updateFilter("purchaseType", "")} className="hover:text-purple-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {(queryState.dateFrom || queryState.dateTo) && (
                <span className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full border border-gray-200">
                  {queryState.dateFrom || "…"} → {queryState.dateTo || "…"}
                  <button onClick={() => { updateFilter("dateFrom", ""); updateFilter("dateTo", ""); }} className="hover:text-gray-400">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* ─── Table Card ─── */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
        >
          {/* Table header strip */}
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 bg-gray-50/60">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-4 bg-gradient-to-b from-[#FF6C6C] to-pink-400 rounded-full" />
              <span className="text-sm font-semibold text-gray-700">
                {isFetching ? "Loading…" : `${pagination?.total ?? orders.length} orders`}
              </span>
              {isFetching && (
                <div className="w-4 h-4 border-2 border-[#FF6C6C] border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            {hasActiveFilters && (
              <span className="text-xs text-gray-400 italic">Filtered results</span>
            )}
          </div>

          {isInitialLoading ? (
            <div className="overflow-x-auto">
              <table className="w-full whitespace-nowrap">
                <thead className="bg-gray-50/50 border-b border-gray-100">
                  <tr>
                    {["Order Code", "Customer", "Items", "Fulfillment", "Amount", "Status", "Date", ""].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
                </tbody>
              </table>
            </div>
          ) : orders.length === 0 ? (
            <OrdersEmptyState
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              onRefresh={handleManualRefresh}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Order Code
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Customer
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Items
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Fulfillment
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Amount
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Status
                      </th>
                      <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Date
                      </th>
                      <th className="px-5 py-3.5 text-right text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {orders.map((order, idx) => (
                      <motion.tr
                        key={order.order_code}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.03, duration: 0.25 }}
                        className="group hover:bg-[#FF6C6C]/[0.025] transition-colors"
                      >
                        {/* Order Code */}
                        <td className="px-5 py-4">
                          <button
                            onClick={() => copyToClipboard(order.order_code)}
                            className="inline-flex items-center gap-1.5 group/copy"
                            title="Copy order code"
                          >
                            <span className="font-mono text-xs font-semibold text-gray-600 bg-gray-50 border border-gray-100 group-hover/copy:border-[#FF6C6C]/30 rounded-lg px-2.5 py-1.5 transition-colors leading-none">
                              {order.order_code}
                            </span>
                            <Copy className="w-3 h-3 text-gray-300 group-hover/copy:text-[#FF6C6C] transition-colors flex-shrink-0" />
                          </button>
                        </td>

                        {/* Customer */}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5 max-w-[200px]">
                            <CustomerAvatar name={order.user?.full_name} />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {order.user?.full_name || "—"}
                              </div>
                              <div className="text-xs text-gray-400 truncate">
                                {order.user?.email || order.delivery_address?.email || "—"}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Items */}
                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                            <Package className="w-3 h-3" />
                            {order.vendor_item_count ?? "—"}
                            <span className="font-normal text-gray-400">/ {order.total_order_items ?? "—"}</span>
                          </span>
                        </td>

                        {/* Fulfillment */}
                        <td className="px-5 py-4">
                          <FulfillmentBadge type={order.fulfillment_type} />
                        </td>

                        {/* Amount */}
                        <td className="px-5 py-4">
                          <div className="text-sm font-bold text-gray-900">
                            {formatCurrency(order.summary?.subtotal)}
                          </div>
                          {order.summary?.total_items && (
                            <div className="text-[11px] text-gray-400">
                              {order.summary.total_items} item{order.summary.total_items !== 1 ? "s" : ""}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="px-5 py-4">
                          <OrderStatusBadge status={order.status} />
                        </td>

                        {/* Date */}
                        <td className="px-5 py-4">
                          <div className="text-sm text-gray-700 font-medium">
                            {order.created_at ? moment(order.created_at).format("DD MMM YYYY") : "—"}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {order.created_at ? moment(order.created_at).format("hh:mm A") : ""}
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-5 py-4 text-right">
                          <OrderActionMenu
                            order={order}
                            onViewItems={handleViewItems}
                            onUpdateStatus={handleUpdateStatus}
                          />
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Footer totals bar */}
              <div className="px-5 py-3 bg-gray-50/60 border-t border-gray-100 flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="text-xs text-gray-500">
                  Page subtotal:{" "}
                  <span className="font-semibold text-gray-800">{formatCurrency(stats.pageRevenue)}</span>
                </span>
                {stats.cancelledCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-500">
                    <XCircle className="w-3 h-3" />
                    {stats.cancelledCount} cancelled on this page
                  </span>
                )}
                {stats.pendingCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-amber-500">
                    <Clock className="w-3 h-3" />
                    {stats.pendingCount} pending payment
                  </span>
                )}
              </div>

              <OrdersPagination
                pagination={pagination}
                isFetching={isFetching}
                onPageChange={goToPage}
                perPage={queryState.perPage}
                onPerPageChange={handlePerPageChange}
              />
            </>
          )}
        </motion.div>
      </div>

      {/* ─── Modals ─── */}
      <OrderDetailsModal
        isOpen={viewModalOpen}
        orderCode={viewOrder?.order_code}
        initialStatus={viewOrder?.status}
        onClose={() => { setViewModalOpen(false); setViewOrder(null); }}
        onUpdateStatus={() => { setViewModalOpen(false); handleUpdateStatus(viewOrder); }}
      />

      <UpdateOrderStatusModal
        isOpen={statusModalOpen}
        order={statusOrder}
        onClose={() => { setStatusModalOpen(false); setStatusOrder(null); }}
        onUpdated={handleStatusUpdated}
      />
    </div>
  );
};

export default Orders;
